import React, { useState, useEffect, useCallback } from 'react';
import styled, { keyframes } from 'styled-components';
import { useLocation } from 'react-router-dom';
import {
  autoUpdate,
  offset,
  useDismiss,
  UseDismissProps,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import { flip, shift } from '@floating-ui/dom';
import { Z_INDEX } from '../../../constants';

const scaleIn = keyframes`
    from {
        opacity: 0;
    }
    to {
        opacity: 1;
    }
`;

const PopUpMenuContainer = styled.div<{}>`
  background-color: var(--color-bg-primary);
  border-radius: 12px;
  border: 1px solid var(--color-grey-8);
  box-shadow: 0px 4px 6px 0px rgba(0, 0, 0, 0.09);
  z-index: ${Z_INDEX.INLINE_DIALOG};

  animation: ${scaleIn} 0.1s ease;
`;

interface PopUpMenuProps {
  trigger: ({ isOpen }: { isOpen: boolean }) => React.ReactNode;
  children: ({ close }: { close: () => void }) => React.ReactNode;
  position?:
    | 'top'
    | 'bottom'
    | 'left'
    | 'right'
    | 'right-start'
    | 'right-end'
    | 'bottom-start'
    | 'bottom-end';
}

const TEST_DISMISS_PROPS: UseDismissProps = {
  capture: true,
  referencePress: true,
  referencePressEvent: 'click',
  outsidePress: true,
  outsidePressEvent: 'click',
};

const PopupMenu: React.FC<PopUpMenuProps> = ({
  trigger,
  children,
  position = 'bottom',
}) => {
  const { pathname } = useLocation();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const open = useCallback(() => {
    setIsOpen(true);
  }, []);
  const close = useCallback(() => {
    setIsOpen(false);
  }, []);

  // Close on navigation
  useEffect(() => {
    setIsOpen(false);
  }, [pathname]);

  const { refs, floatingStyles, context } = useFloating({
    whileElementsMounted: autoUpdate,
    open: isOpen,
    onOpenChange: setIsOpen,
    placement: position || 'bottom-start',
    strategy: 'fixed',
    middleware: [flip(), shift(), offset(4)],
  });

  const dismiss = useDismiss(
    context,
    process.env.NODE_ENV === 'test' ? TEST_DISMISS_PROPS : undefined,
  );
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);

  return (
    <div>
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        onClick={isOpen ? close : open}
      >
        {trigger({ isOpen })}
      </div>

      {isOpen && (
        <PopUpMenuContainer
          ref={refs.setFloating}
          style={floatingStyles}
          {...getFloatingProps()}
        >
          <div>{children({ close })}</div>
        </PopUpMenuContainer>
      )}
    </div>
  );
};

export default PopupMenu;
